import type { Alias } from '@ps/web-core-store';
import { select } from '@ps/web-core-store';
import { absoluteURL, generatePath } from '@ps/web-core-utils';
import type { EventPageTabEntities, RacePageTabEntities } from '@ps/web-shared-analytics';
import { useTrackers } from '@ps/web-shared-analytics';
import type { LocationDescriptor, LocationDescriptorObject } from 'history';
import type React from 'react';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import type { NavLinkProps } from 'react-router-dom';
import { NavLink, Link as RouterLink } from 'react-router-dom';
import URLParse, { qs } from 'url-parse';

import CrossPlatformLink from '../CrossPlatformLink/CrossPlatformLink';
import type { Props as ImportedCrossPlatformLinkProps } from '../CrossPlatformLink/CrossPlatformLink';
import type { ValuesProp } from '../types/Props';

type CrossPlatformLinkProps = ImportedCrossPlatformLinkProps & {
  isCrossPlatform: true;
  activeClassName?: never;
  isActive?: never;
  alias?: never;
  searchParams?: never;
  track?: never;
  trackText?: never;
  values?: never;
  hash?: string;
  entities?: EventPageTabEntities | RacePageTabEntities;
};

export type LinkProps = Partial<NavLinkProps> &
  Partial<ValuesProp> & {
    isCrossPlatform?: never;
    openLinkInNewTab?: never;
    href?: string;
    activeClassName?: string;
    isActive?: NavLinkProps['isActive'];
    alias?: Alias;
    searchParams?: URLSearchParams;
    track?: boolean;
    trackText?: string;
    hash?: string;
    entities?: EventPageTabEntities | RacePageTabEntities;
  };

export type Props = LinkProps | CrossPlatformLinkProps;

const Link: React.FC<Props> = ({
  isCrossPlatform,
  openLinkInNewTab,
  href = '',
  values,
  alias,
  isActive,
  activeClassName,
  onClick,
  searchParams,
  track,
  trackText,
  hash,
  entities,
  ...props
}) => {
  const { siteId } = useSelector(select.location.getQuery);
  const aliases = useSelector(select.cms.getAliases);
  const locale = useSelector(select.locale.get);
  const { linkClicked } = useTrackers();
  const linkRef = useRef<HTMLAnchorElement>(null);
  let target: string | LocationDescriptor = href;

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick && onClick(event);
    const linkData = {
      destinationUrl: (target as LocationDescriptorObject).pathname || (target as string),
      linkText: trackText || (locale === 'en' ? linkRef.current?.innerText : undefined)
    };

    track !== false && linkClicked(linkData);
  };

  if (isCrossPlatform) {
    return (
      <CrossPlatformLink
        {...(props as CrossPlatformLinkProps)}
        href={href}
        openLinkInNewTab={openLinkInNewTab}
        onClick={handleOnClick}
      />
    );
  }

  if (absoluteURL(href) || href.startsWith('#')) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a
        {...(props as React.DetailedHTMLProps<
          React.AnchorHTMLAttributes<HTMLAnchorElement>,
          HTMLAnchorElement
        >)}
        href={href}
        onClick={handleOnClick}
        ref={linkRef}
      />
    );
  }

  if (alias) {
    if (!aliases[alias]) return null;
    target = `${generatePath(
      aliases[alias] as string,
      values as Parameters<typeof generatePath>[1]
    )}${hash ? `#${hash}` : ''}`;
  }

  if (siteId || searchParams) {
    const targetUrl = new URLParse(target);
    const targetUrlParams = qs.stringify({
      // The types for URLParse.query are invalid. It's actually not an object, but a string
      ...qs.parse(targetUrl.query as unknown as string),
      ...qs.parse(searchParams?.toString() ?? ''),
      ...(siteId?.length ? { siteId } : {})
    });

    target = { pathname: targetUrl.pathname, search: targetUrlParams, hash };
  }

  return activeClassName ? (
    <NavLink
      {...props}
      to={target}
      activeClassName={activeClassName}
      isActive={isActive}
      ref={linkRef}
      onClick={handleOnClick}
    />
  ) : (
    <RouterLink
      {...(props as Parameters<RouterLink>)}
      to={target}
      ref={linkRef}
      onClick={handleOnClick}
    />
  );
};

export default Link;
